import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  Tooltip
} from '@mui/material'
import ArrowIcon from '@mui/icons-material/ArrowDropDown'
import { TextField } from '@carsys/ui'
import { IGraduatedPrice, IProduct } from '@cbgms/api'
import Currency from '@cbgms/components/general/Currency'
import { useOrderButtonContext } from '@cbgms/components/src/order-parts/context/buttons/OrderButtonContext'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import { makeStyles } from '@mui/styles'
import { FromPrice, FromPriceNew } from '@cbgms/components/src/order-list/price/FromPrice'
import { useFeature } from '@cbgms/base/feature-flags'
import useQuantityExceedsMaxStockWarningMessage from '../../hooks/use-quantity-exceeds-max-stock-warning-message'

const useStyles = makeStyles(theme => ({
  list: {
    textAlign: 'left',
    '& > li:last-of-type > div': {
      // remove the border-bottom from the last button in the list of graduated pricese
      borderBottom: 'none'
    }
  },
  itemIcon: {
    minWidth: theme.spacing(4)
  },
  itemButton: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.LKQGrey.border.modest}`
  },
  header: {
    paddingTop: theme.spacing(1.5) /* needs .5 extra spacing to align with the .5 padding of childs */,
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    lineHeight: '1rem'
  }
}))

interface Props {
  product: IProduct
  hasOrderLine: boolean
  currentLineQuantity?: number
  isLoading?: boolean
  graduatedPrices?: IGraduatedPrice[]
  hideLabels?: boolean
  onlyLabels?: boolean
}

export const GraduatedPricesFromSelector: React.FC<Props> = memo(
  ({ product, hasOrderLine, isLoading = false, graduatedPrices, hideLabels, onlyLabels }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
      ;(document.activeElement as HTMLElement).blur()
    }
    const handleClose = () => setAnchorEl(null)
    const open = Boolean(anchorEl)

    const { onClickNewOrderLine, onClickUpdateOrderLine, isButtonLocked, isOpEqualOpPromotionType, orderLine } = useOrderButtonContext()
    const getQuantityPostQuantityExceedsMaxStockValidation = useQuantityExceedsMaxStockWarningMessage()
    const hasMoreProductOnScreenDesign = useFeature('more-products-on-screen-design-v2')
    const loading = isButtonLocked || isLoading

    async function handleUpdateLine(exactQuantity: number) {
      handleClose()
      const updatedQuantity = getQuantityPostQuantityExceedsMaxStockValidation(
        exactQuantity,
        isOpEqualOpPromotionType,
        product?.TotalStock || 0
      )
      if (hasOrderLine) {
        if (updatedQuantity === orderLine?.Quantity) {
          return
        }

        await onClickUpdateOrderLine(updatedQuantity)
      } else {
        await onClickNewOrderLine(updatedQuantity)
      }
    }

    if (!product.GraduatedPrices?.length) return null

    return !hasMoreProductOnScreenDesign ? (
      <FromPrice graduatedPrices={product?.GraduatedPrices} hideLabels={hideLabels} />
    ) : (
      <FromPriceNew
        loading={loading}
        handleClick={handleClick}
        handleClose={handleClose}
        open={open}
        anchorEl={anchorEl}
        graduatedPrices={graduatedPrices}
        hideLabels={hideLabels}
        onlyLabels={onlyLabels}
      >
        <GraduatedPricesList product={product} handleUpdateLine={handleUpdateLine} />
      </FromPriceNew>
    )
  }
)
export const GraduatedPricesSelector: React.FC<Props> = memo(({ product, hasOrderLine, isLoading = false, currentLineQuantity = 0 }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    ;(document.activeElement as HTMLElement).blur()
  }
  const handleClose = () => setAnchorEl(null)
  const open = Boolean(anchorEl)

  const { onClickNewOrderLine, onClickUpdateOrderLine, isButtonLocked, isOpEqualOpPromotionType, orderLine } = useOrderButtonContext()
  const getQuantityPostQuantityExceedsMaxStockValidation = useQuantityExceedsMaxStockWarningMessage()
  const loading = isButtonLocked || isLoading

  async function handleUpdateLine(exactQuantity: number) {
    handleClose()
    const updatedQuantity = getQuantityPostQuantityExceedsMaxStockValidation(
      exactQuantity,
      isOpEqualOpPromotionType,
      product?.TotalStock || 0
    )
    if (hasOrderLine) {
      if (updatedQuantity === orderLine?.Quantity) {
        return
      }

      await onClickUpdateOrderLine(updatedQuantity)
    } else {
      await onClickNewOrderLine(updatedQuantity)
    }
  }

  if (!product.GraduatedPrices?.length) return null

  return (
    <GraduatedPricesSelect
      loading={loading}
      handleClick={handleClick}
      handleClose={handleClose}
      open={open}
      anchorEl={anchorEl}
      currentLineQuantity={currentLineQuantity}
      product={product}
    >
      <GraduatedPricesList product={product} handleUpdateLine={handleUpdateLine} />
    </GraduatedPricesSelect>
  )
})

export const GraduatedPricesSelect: React.FC<{
  currentLineQuantity: number
  product: IProduct
  open: boolean
  loading: boolean
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
  handleClose: () => void
  anchorEl: HTMLElement | null
}> = ({ children, currentLineQuantity, product, open, loading, handleClick, handleClose, anchorEl }) => {
  const { t } = useTranslation()
  const bulkValue = React.useMemo(
    () => product.GraduatedPrices.find(p => p.MinimumQuantity === currentLineQuantity)?.MinimumQuantity || 0,
    [product.GraduatedPrices, currentLineQuantity]
  )

  return (
    <>
      <TextField
        sx={{ ml: 1, width: '5rem' }}
        data-testid='graduated-price-selector'
        fullWidth={false}
        value={bulkValue}
        onClick={handleClick}
        label={t('Order.GraduatedPricesSelectLabel')}
        helperText={null}
        InputProps={{
          inputProps: { sx: { height: '100%', cursor: 'pointer' } },
          sx: { pr: 0, cursor: 'pointer' },
          endAdornment: (
            <Box display='flex' height='100%' width='100%' justifyContent='center' alignItems='center'>
              {loading ? <CircularProgress size='1rem' /> : <ArrowIcon data-testid='graduated-price-down-arrow-icon' />}
            </Box>
          )
        }}
        focused={false}
      />
      <Popover data-testid='graduated-prices-popover' open={open} anchorEl={anchorEl} onClose={handleClose}>
        {children}
      </Popover>
    </>
  )
}

export const GraduatedPricesList: React.FC<{ product: IProduct; handleUpdateLine: (exactQuantity: number) => Promise<void> }> = ({
  product,
  handleUpdateLine
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <List
      className={classes.list}
      subheader={<ListSubheader className={classes.header}>{t('Order.GraduatedPriceListTitle')}</ListSubheader>}
    >
      {product.GraduatedPrices.map((gp, index) => (
        <GraduatedPriceListItem key={index} graduatedPrice={gp} handleUpdateLine={handleUpdateLine} />
      ))}
    </List>
  )
}

const GraduatedPriceListItem: React.FC<{
  graduatedPrice: IGraduatedPrice
  handleUpdateLine: (exactQuantity: number) => Promise<void>
}> = ({ graduatedPrice, handleUpdateLine }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const label = (
    <Box display='flex' justifyContent='space-between'>
      <Box sx={{ minWidth: '1.5rem' }}>{graduatedPrice.MinimumQuantity}</Box>
      <Box sx={{ pl: 4 }}>
        <Currency amount={graduatedPrice.Price} />
      </Box>
    </Box>
  )

  return (
    <Tooltip placement='left' title={t('Order.GraduatedPriceAmount', { amount: graduatedPrice.MinimumQuantity })}>
      <ListItem onClick={() => handleUpdateLine(graduatedPrice.MinimumQuantity)} disablePadding>
        <ListItemButton className={classes.itemButton}>
          <ListItemIcon className={classes.itemIcon}>
            <AddShoppingCartIcon color='primary' />
          </ListItemIcon>
          <ListItemText data-testid='graduated-price-chip'>{label}</ListItemText>
        </ListItemButton>
      </ListItem>
    </Tooltip>
  )
}
